// owner/alerts/ThisDeviceAlerts.js — «هذا الجهاز» under the alert switches in settings (§10 "8d", Adil 5 Nov 2026).
//   - a computer's browser: turn alerts on, send a test, turn them off — through the web's channel;
//   - the store app: this phone's alerts (on / not allowed, with where to change it) and a test;
//   - a phone's browser: nothing but the switches above, plus «get the app» once the server has a store link.
// The switches above choose WHICH alerts; this part says whether THIS device receives them.
// Results and failures use the SHARED alert words (config/notificationLabels.js — the staff panel's): a test result
// says "sent", never "delivered" (TEMPORAL_FIELD_CATALOGUE, AppDevice.lastSuccessfulAcceptance), and refusals are
// translated from their codes (I18N_STRATEGY), the 5-a-minute test limit included.

import React, { useState } from 'react';
import useLanguage from '../../hooks/useLanguage';
import Icon from '../ui/Icon';
import { InlineError } from '../ui/States';
import useOwnerFormat from '../ui/useOwnerFormat';
import { messageForCode, messageForError, subscribeFailureMessage, testSentMessage } from '../../config/notificationLabels';
import { useDeviceAlerts } from './deviceAlerts';
import { useStoreLink } from '../api/ownerReads';
import { useSendTestAlert } from '../api/ownerWrites';

const GetTheApp = ({ platform }) => {
  const { t } = useLanguage();
  const storeLink = useStoreLink(platform);
  if (!storeLink.data) return null;
  return (
    <p className="o-footnote">
      {t('ownerApp.settings.device.getAppHint')}{' '}
      <a className="o-mail" href={storeLink.data} target="_blank" rel="noopener noreferrer">{t('ownerApp.settings.device.getApp')}</a>
    </p>
  );
};

/** Why turning alerts on did not work, in the reader's language; null when the state itself says it (not allowed). */
const enableFailure = (t, result, kind) => {
  if (result.reason === 'denied') return null;
  if (result.error) return messageForError(t, result.error);
  if (result.code) return messageForCode(t, result.code) || t('notifications.errors.generic');
  if (kind === 'browser') return subscribeFailureMessage(t, result.reason);
  return t('notifications.errors.generic');
};

const ThisDeviceAlerts = () => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const device = useDeviceAlerts({ refreshOnMount: true });
  const test = useSendTestAlert();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  if (device.kind === 'none') return null;
  if (device.kind === 'phoneBrowser') return <GetTheApp platform={device.storePlatform} />;

  const where = device.kind === 'app' ? 'app' : 'browser';
  const words = (key) => t(`ownerApp.settings.device.${where}.${key}`);
  const { status } = device;

  const run = (action) => async () => {
    setBusy(true);
    setProblem('');
    test.reset();
    try {
      const result = await action();
      if (result && result.ok === false) setProblem(enableFailure(t, result, device.kind) || '');
    } catch (error) {
      setProblem(t('notifications.errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (status === 'checking') return <div className="o-sk o-device-sk" aria-busy="true" />;
  if (status === 'error') {
    return (
      <div className="o-sheet o-device">
        <InlineError text={t('notifications.status.unreadableTitle')} onRetry={() => device.refresh()} />
      </div>
    );
  }

  const headline = {
    on: words('on'), off: words('off'), prompt: words('off'), denied: words('denied'),
    unsupported: words('unsupported'), unavailable: t('ownerApp.settings.alertsUnavailable')
  }[status] || words('off');
  const detail = { on: words('onHint'), off: words('offHint'), prompt: words('offHint'), denied: words('deniedHint') }[status];

  return (
    <div className={`o-sheet o-device${status === 'on' ? ' is-on' : ''}`}>
      <div className="o-device-head">
        <span className="o-device-glyph" aria-hidden="true"><Icon name={status === 'on' ? 'check' : 'bell'} size="sm" /></span>
        <div className="o-body">
          <p className="o-t">{headline}</p>
          {detail && <p className="o-m">{detail}</p>}
        </div>
      </div>

      {(status === 'off' || status === 'prompt') && (
        <button type="button" className="o-btn o-btn-primary o-btn-block" onClick={run(device.enable)} disabled={busy}>
          {busy ? t('notifications.enabling') : t('notifications.enable')}
        </button>
      )}

      {status === 'on' && (
        <div className="o-device-actions">
          <button type="button" className="o-btn o-btn-outline" onClick={() => { setProblem(''); test.mutate(); }}
            disabled={busy || test.isPending}>
            {test.isPending ? t('notifications.testing') : t('ownerApp.settings.device.test')}
          </button>
          {device.kind === 'browser' && (
            <button type="button" className="o-btn o-btn-ghost" onClick={run(device.disable)} disabled={busy}>
              {t('ownerApp.settings.device.browser.turnOff')}
            </button>
          )}
        </div>
      )}

      {test.isSuccess && <p className="o-form-ok" role="status">{testSentMessage(t, test.data?.accepted || 0, f.count)}</p>}
      {test.isError && <p className="o-form-error" role="alert">{messageForError(t, test.error)}</p>}
      {problem && <p className="o-form-error" role="alert">{problem}</p>}
    </div>
  );
};

export default ThisDeviceAlerts;
